import React, { useState, useEffect } from 'react';
import Navbar from '../components/NavBar';
import ClubCard from '../components/ClubCard';
import { getMyCreatedClubs, deleteClub } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  MapPin, 
  Clock, 
  Calendar, 
  User, 
  Edit, 
  Trash2, 
  Plus, 
  Crown, 
  Users, 
  TrendingUp,
  Eye,
  Filter,
  Grid,
  List as ListIcon
} from 'lucide-react';
import { DotLoader } from 'react-spinners';

function MyCreatedClubs() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMyCreatedClubs();
  }, [isAuthenticated, navigate]);

  const fetchMyCreatedClubs = async () => {
    try {
      const data = await getMyCreatedClubs();
      setClubs(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId, clubName) => {
    const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer "${clubName}" ? Cette action est irréversible.`);
    if (!confirmed) return;

    try {
      await deleteClub(clubId);
      fetchMyCreatedClubs();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // Calculer les statistiques
  const stats = {
    totalClubs: clubs.length,
    totalMembers: clubs.reduce((sum, club) => sum + (club.nombreMembres || club.membres?.length || 0), 0),
    totalSeances: clubs.reduce((sum, club) => sum + (club.nombreSeances || club.seances?.length || 0), 0),
    avgMembers: clubs.length > 0 
      ? Math.round(clubs.reduce((sum, club) => sum + (club.nombreMembres || club.membres?.length || 0), 0) / clubs.length)
      : 0,
    levels: clubs.reduce((acc, club) => {
      acc[club.niveau] = (acc[club.niveau] || 0) + 1;
      return acc;
    }, {})
  };

  // Filtrer les clubs
  const filteredClubs = filterLevel === 'all' 
    ? clubs 
    : clubs.filter(club => club.niveau === filterLevel);

  // Obtenir les niveaux uniques
  const uniqueLevels = [...new Set(clubs.map(club => club.niveau))];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header avec bouton créer */}
        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                <Crown className="text-yellow-500 fill-current" size={28} />
                Mes Clubs Créés
              </h2>
              <p className="text-sm sm:text-base text-gray-400 mt-1">Les clubs que vous avez créés et gérez</p>
            </div>
            
            {/* Bouton créer - Desktop */}
            <Link
              to="/create"
              className="hidden sm:flex items-center space-x-2 bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-3 rounded-xl transition-all font-semibold shadow-lg shadow-indigo-500/20 active:scale-95"
            >
              <Plus size={20} />
              <span>Créer un club</span>
            </Link>
          </div>

          {/* Statistiques - Responsive */}
          {clubs.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-4 hover:border-yellow-500 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Clubs créés</p>
                    <p className="text-2xl sm:text-3xl font-bold text-yellow-500">{stats.totalClubs}</p>
                  </div>
                  <Crown size={32} className="text-yellow-900/30 fill-current hidden sm:block" />
                  <Crown size={24} className="text-yellow-900/30 fill-current sm:hidden" />
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-4 hover:border-purple-500 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Total membres</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-500">{stats.totalMembers}</p>
                  </div>
                  <Users size={32} className="text-purple-900/30 hidden sm:block" />
                  <Users size={24} className="text-purple-900/30 sm:hidden" />
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-4 hover:border-blue-500 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Moy. membres</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-500">{stats.avgMembers}</p>
                  </div>
                  <TrendingUp size={32} className="text-blue-900/30 hidden sm:block" />
                  <TrendingUp size={24} className="text-blue-900/30 sm:hidden" />
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-4 hover:border-green-500 transition-all col-span-2 lg:col-span-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Séances/sem</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-500">{stats.totalSeances}</p>
                  </div>
                  <Calendar size={32} className="text-green-900/30 hidden sm:block" />
                  <Calendar size={24} className="text-green-900/30 sm:hidden" />
                </div>
              </div>
            </div>
          )}

          {/* Filtres et Toggle View */}
          {clubs.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="md:hidden flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
                >
                  <Filter size={16} />
                  <span>Filtres</span>
                </button>
                
                <div className={`${showFilters ? 'flex' : 'hidden'} md:flex items-center gap-2 flex-wrap`}>
                  <button
                    onClick={() => setFilterLevel('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      filterLevel === 'all'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-gray-300'
                    }`}
                  >
                    Tous ({clubs.length})
                  </button>
                  {uniqueLevels.map(level => (
                    <button
                      key={level}
                      onClick={() => setFilterLevel(level)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        filterLevel === level
                          ? 'bg-indigo-500 text-white'
                          : 'bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-gray-300'
                      }`}
                    >
                      {level} ({stats.levels[level] || 0})
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    viewMode === 'grid' 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Grid size={16} />
                  <span>Grille</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    viewMode === 'list' 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <ListIcon size={16} />
                  <span>Liste</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Liste des clubs */}
        {filteredClubs.length > 0 ? (
          <div className={`grid gap-3 sm:gap-4 md:gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredClubs.map(club => (
              <div key={club._id} className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg overflow-hidden hover:border-indigo-500 transition-all">
                {/* Image ou placeholder */}
                {club.imageClub ? (
                  <Link to={`/club/${club._id}`}>
                    <div className="h-32 sm:h-40 relative overflow-hidden group">
                      <img
                        src={club.imageClub}
                        alt={club.nom}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-2 right-2">
                        <span className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-white px-2.5 py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                          {club.niveau}
                        </span>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <Link to={`/club/${club._id}`}>
                    <div className="h-32 sm:h-40 bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center relative group">
                      <Crown size={48} className="text-slate-700" />
                      <div className="absolute top-2 right-2">
                        <span className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-white px-2.5 py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                          {club.niveau}
                        </span>
                      </div>
                    </div>
                  </Link>
                )}

                <div className="p-4 sm:p-5">
                  <Link to={`/club/${club._id}`}>
                    <div className="flex justify-between items-start mb-3 group cursor-pointer">
                      <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-indigo-500 transition-colors flex-1">
                        {club.nom}
                      </h3>
                      <Eye size={18} className="text-gray-400 group-hover:text-indigo-500 transition-colors ml-2 flex-shrink-0" />
                    </div>
                  </Link>

                  <p className="text-sm sm:text-base text-gray-400 mb-4 line-clamp-2">{club.description}</p>

                  {/* Stats en grid */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="flex flex-col items-center bg-slate-800 border border-slate-700 p-2 rounded-lg">
                      <MapPin size={16} className="text-indigo-500 mb-1" />
                      <span className="text-xs text-gray-400 truncate w-full text-center">{club.ville}</span>
                    </div>
                    <div className="flex flex-col items-center bg-slate-800 border border-slate-700 p-2 rounded-lg">
                      <User size={16} className="text-indigo-500 mb-1" />
                      <span className="text-xs font-bold text-white">{club.nombreMembres || club.membres?.length || 0}</span>
                    </div>
                    <div className="flex flex-col items-center bg-slate-800 border border-slate-700 p-2 rounded-lg">
                      <Calendar size={16} className="text-indigo-500 mb-1" />
                      <span className="text-xs font-bold text-white">{club.nombreSeances || club.seances?.length || 0}</span>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex gap-2">
                    <Link
                      to={`/club/${club._id}`}
                      className="flex-1 flex items-center justify-center space-x-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white py-2 sm:py-2.5 rounded-lg active:scale-95 transition-all font-semibold text-sm"
                    >
                      <Eye size={16} />
                      <span>Voir</span>
                    </Link>
                    <Link
                      to={`/edit-club/${club._id}`}
                      className="flex-1 flex items-center justify-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white py-2 sm:py-2.5 rounded-lg active:scale-95 transition-all font-semibold text-sm shadow-lg"
                    >
                      <Edit size={16} />
                      <span>Modifier</span>
                    </Link>
                    <button
                      onClick={() => handleDeleteClub(club._id, club.nom)}
                      className="flex items-center justify-center space-x-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-2 sm:py-2.5 rounded-lg active:scale-95 transition-all font-semibold text-sm shadow-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : clubs.length === 0 ? (
          // Aucun club créé
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-6 sm:p-8 max-w-md mx-auto">
              <div className="text-6xl sm:text-7xl mb-4">👑</div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Créez votre premier club !
              </h3>
              <p className="text-sm sm:text-base text-gray-400 mb-6">
                Devenez leader d'une communauté de runners et organisez vos propres séances d'entraînement.
              </p>
              <Link
                to="/create"
                className="inline-flex items-center space-x-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl transition-all font-semibold shadow-lg shadow-indigo-500/20 active:scale-95"
              >
                <Plus size={20} />
                <span>Créer mon premier club</span>
              </Link>
            </div>
          </div>
        ) : (
          // Aucun club pour ce filtre
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-lg p-6 sm:p-8 max-w-md mx-auto">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                Aucun club trouvé
              </h3>
              <p className="text-sm sm:text-base text-gray-400 mb-4">
                Aucun club ne correspond au niveau "{filterLevel}"
              </p>
              <button
                onClick={() => setFilterLevel('all')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2.5 rounded-lg transition font-semibold text-sm sm:text-base shadow-lg shadow-indigo-500/20"
              >
                Voir tous les clubs
              </button>
            </div>
          </div>
        )}

        {/* Bouton flottant pour créer un club (mobile) */}
        <Link
          to="/create"
          className="md:hidden fixed bottom-6 right-6 bg-indigo-500 hover:bg-indigo-600 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/20 transition-all transform hover:scale-110 active:scale-95 z-40 flex items-center justify-center"
          aria-label="Créer un club"
        >
          <Plus size={24} strokeWidth={3} />
        </Link>
      </main>
    </div>
  );
}

export default MyCreatedClubs;